<template>
  <div ref="timelineEl" class="activity-timeline">
    <!-- Loading State -->
    <div v-if="isLoading" class="has-text-centered py-4">
      <BrandLoader :size="'2x'" />
      <span class="ml-2">Loading activity...</span>
    </div>

    <!-- Activity List -->
    <div v-else-if="activities.length > 0" class="timeline">
      <template v-for="row in rows" :key="row.key">
        <!-- Quiet-period divider: makes a stall visible instead of implied -->
        <div v-if="row.type === 'gap'" class="tl-gap">
          <span>{{ row.label }}</span>
        </div>

        <div v-else class="tl-entry">
          <div
            class="tl-row"
            :class="{ 'is-expandable': row.log.count > 1 }"
            @click="row.log.count > 1 && toggleExpanded(row.log)"
          >
            <span class="tl-time">{{ formatTimestamp(row.log.timestamp) }}</span>

            <!-- Identity carries no label — the avatar (or the automation mark)
                 is the whole column, with the name in a hover tooltip. -->
            <span class="tl-who" :data-tippy-content="whoTooltip(row.log)">
              <span v-if="isAutomated(row.log)" class="tl-bot">
                <font-awesome-icon :icon="['fal', 'bolt']" />
              </span>
              <UserAvatar v-else :user="userFor(row.log)" size="small" no-tooltip />
            </span>

            <span class="tl-action">{{ groupLabel(row.log) }}</span>

            <span
              v-if="row.log.count === 1 && shouldShowValueChange(row.log)"
              class="tl-change"
              :data-tippy-content="tooltipIfLong(changeText(row.log))"
            >
              {{ formatInlineValue(row.log.field, row.log.oldValue) }}
              <font-awesome-icon :icon="['fal', 'arrow-right']" class="mx-1" />
              <strong>{{ formatInlineValue(row.log.field, row.log.newValue) }}</strong>
            </span>

            <span v-else class="tl-detail" :data-tippy-content="tooltipIfLong(detailText(row.log))">
              {{ detailText(row.log) }}
            </span>

            <font-awesome-icon
              v-if="row.log.count > 1"
              :icon="['fas', 'chevron-right']"
              :active-icon="['fas', 'chevron-down']"
              :active="isExpanded(row.log)"
              class="tl-caret"
            />
          </div>

          <!-- Collapsed run, expanded -->
          <div v-if="row.log.count > 1 && isExpanded(row.log)" class="tl-children">
            <div v-for="child in row.log.children" :key="child.id" class="tl-child">
              <span class="tl-time">{{ formatTimestamp(child.timestamp) }}</span>
              <span class="tl-detail">{{ child.note || getActionDescription(child) }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Load More Button -->
      <div v-if="hasMore && !compact" class="has-text-centered mt-4">
        <button
          class="button is-small is-primary"
          @click="loadMore"
        >
          Show More Activity
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="has-text-centered py-3 has-text-grey">
      <span class="icon">
        <font-awesome-icon :icon="['fal', 'clock-rotate-left']" />
      </span>
      <p class="mt-2 is-size-7">No activity yet</p>
    </div>
  </div>
</template>

<script setup>
import BrandLoader from '@/components/global/BrandLoader.vue';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { delegate } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import UserAvatar from '@/components/common/UserAvatar.vue';
import { valuesEqual } from '@/utils/valueCompare';

// Props
const props = defineProps({
  activities: {
    type: Array,
    required: true,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  },
  limit: {
    type: Number,
    default: 10
  },
  userCache: {
    type: Map,
    default: () => new Map()
  }
});

// State
const displayLimit = ref(props.limit);
const expandedIds = ref(new Set());
const timelineEl = ref(null);
let tooltipDelegate = null;

// One delegated tippy for the whole list instead of an instance per avatar —
// rows come and go as runs expand and "Show More" loads, and delegation picks
// up new ones for free. UserAvatar's own tooltip is suppressed via no-tooltip.
onMounted(() => {
  if (!timelineEl.value) return;
  tooltipDelegate = delegate(timelineEl.value, {
    target: '[data-tippy-content]',
    theme: 'dark',
    placement: 'top',
    animation: 'scale',
    duration: [200, 150]
  });
});

onBeforeUnmount(() => {
  tooltipDelegate?.destroy?.();
});

// Activity logs carry no rule foreign key — an automated field change and the
// `automation_executed` entry that caused it are only related by timestamp.
// Same ±5s pairing the diagnoseDeliverableReview.js script uses.
const RULE_ATTRIBUTION_WINDOW_MS = 5000;
// Consecutive same-actor/same-action entries collapse only inside this window,
// so a burst of comments folds up but two separate sessions stay separate.
const GROUP_WINDOW_MS = 60 * 60 * 1000;
// A silence longer than this gets called out with a divider.
const QUIET_GAP_MS = 24 * 60 * 60 * 1000;

// Actions that render an old → new pair, and are therefore the ones that can be
// a no-op worth suppressing.
const VALUE_CHANGE_ACTIONS = [
  'status_change',
  'brief_update',
  'field_update',
  'stage_change',
  'contract_updated'
];

// Only repeatable, low-signal actions collapse. Field/stage/status changes each
// carry distinct information and must never be folded into a count.
const GROUP_LABELS = {
  review_comment_added: 'comments',
  review_reply_added: 'replies',
  review_guest_comment_added: 'guest comments',
  review_guest_reply_added: 'guest replies',
  review_comment_resolved: 'comments resolved',
  review_comment_unresolved: 'comments unresolved',
  review_item_added: 'review items added',
  review_version_added: 'versions added',
  review_internal_approved: 'items approved internally',
  review_client_approved: 'items approved by client'
};

function toMillis(timestamp) {
  if (!timestamp) return null;
  const date = timestamp instanceof Date
    ? timestamp
    : (timestamp.toDate ? timestamp.toDate() : new Date(timestamp));
  const t = date.getTime();
  return Number.isNaN(t) ? null : t;
}

function isAutomated(log) {
  return log._automated === true || log.userName === 'Automation';
}

// Work-pool claims were historically written with their own schema
// (type/description/claimedBy instead of action/note/userId), so they rendered
// as a contentless "System · Activity" row. The writer is fixed; this maps the
// records already in Firestore onto the standard shape.
function normalizeLog(log) {
  if (log.action || !log.type) return log;
  return {
    ...log,
    action: log.automationType === 'work_pool_claim' ? 'work_pool_claim' : log.type,
    note: log.note || log.description || null,
    userId: log.userId || log.claimedBy || log.triggeredBy || null
  };
}

// Services no longer write these (see utils/valueCompare.js), but Firestore is
// full of historical "None → None" and "Jul 27 → Jul 27" entries from when the
// diff used a bare !==. Suppress them at render so the log reads clean now
// rather than after a backfill.
function isNoOpChange(log) {
  if (!VALUE_CHANGE_ACTIONS.includes(log.action)) return false;
  if (log.oldValue === undefined || log.newValue === undefined) return false;
  if (valuesEqual(log.oldValue, log.newValue)) return true;
  // Two different strings are always a real change. Only non-strings (dates,
  // ids, objects) can differ by identity yet render identically — and skipping
  // strings here keeps two long titles sharing a prefix from being merged.
  if (typeof log.oldValue === 'string' && typeof log.newValue === 'string') return false;
  return formatValue(log.field, log.oldValue) === formatValue(log.field, log.newValue);
}

// Computed
// Hide automation_executed logs — their field changes are logged as individual
// entries (stage_change, field_update). Before dropping them, harvest the rule
// name they carry and hand it to the entries they caused, so an automated row
// can say WHICH rule fired instead of an anonymous "Automation".
const attributedActivities = computed(() => {
  const normalized = props.activities.map(normalizeLog);
  const executed = normalized.filter(
    (log) => log.action === 'automation_executed' && log.automationRuleName
  );

  return normalized
    .filter((log) => log.action !== 'automation_executed')
    .filter((log) => !isNoOpChange(log))
    .map((log) => {
      if (!isAutomated(log) || log.automationRuleName) return log;
      const t = toMillis(log.timestamp);
      if (t === null) return log;

      let best = null;
      let bestDelta = RULE_ATTRIBUTION_WINDOW_MS;
      for (const ex of executed) {
        const et = toMillis(ex.timestamp);
        if (et === null) continue;
        const delta = Math.abs(et - t);
        if (delta <= bestDelta) {
          best = ex;
          bestDelta = delta;
        }
      }
      return best ? { ...log, automationRuleName: best.automationRuleName } : log;
    });
});

function canGroup(group, log) {
  if (!GROUP_LABELS[log.action] || group.action !== log.action) return false;
  if ((group.userId || group.userName) !== (log.userId || log.userName)) return false;
  const a = toMillis(group.oldestTimestamp);
  const b = toMillis(log.timestamp);
  return a !== null && b !== null && a - b <= GROUP_WINDOW_MS;
}

// Fold consecutive runs (activities arrive newest-first) into a single row with
// a count + expandable children. Nine identical "Comment Added" cards become one.
const groupedActivities = computed(() => {
  const out = [];
  for (const log of attributedActivities.value) {
    const last = out[out.length - 1];
    if (last && canGroup(last, log)) {
      last.count++;
      last.children.push(log);
      last.oldestTimestamp = log.timestamp;
    } else {
      out.push({ ...log, count: 1, children: [log], oldestTimestamp: log.timestamp });
    }
  }
  return out;
});

const displayedLogs = computed(() => {
  if (props.compact) {
    return groupedActivities.value.slice(0, 5);
  }
  return groupedActivities.value.slice(0, displayLimit.value);
});

const hasMore = computed(() => {
  return groupedActivities.value.length > displayLimit.value;
});

function humanizeGap(gapMs) {
  const days = Math.floor(gapMs / 86400000);
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
  const hours = Math.floor(gapMs / 3600000);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

// Interleave "nothing happened for N days" dividers, including a leading one so
// a deliverable that has been parked since last month says so at the top.
const rows = computed(() => {
  const logs = displayedLogs.value;
  const out = [];
  if (!logs.length) return out;

  const newest = toMillis(logs[0].timestamp);
  if (newest !== null && Date.now() - newest > QUIET_GAP_MS) {
    out.push({
      type: 'gap',
      key: 'gap-now',
      label: `No activity for ${humanizeGap(Date.now() - newest)}`
    });
  }

  logs.forEach((log, i) => {
    out.push({ type: 'entry', key: log.id || `entry-${i}`, log });
    const next = logs[i + 1];
    if (!next) return;
    const a = toMillis(log.oldestTimestamp);
    const b = toMillis(next.timestamp);
    if (a !== null && b !== null && a - b > QUIET_GAP_MS) {
      out.push({
        type: 'gap',
        key: `gap-${log.id || i}`,
        label: `${humanizeGap(a - b)} with no activity`
      });
    }
  });

  return out;
});

// Methods
function loadMore() {
  displayLimit.value += props.limit;
}

function isExpanded(log) {
  return expandedIds.value.has(log.id);
}

function toggleExpanded(log) {
  const next = new Set(expandedIds.value);
  if (next.has(log.id)) next.delete(log.id);
  else next.add(log.id);
  expandedIds.value = next; // new Set so the computed template re-renders
}

// The avatar needs a user object, but activity logs only store userId + userName.
// The cache covers admins; clients and guests fall back to their logged name,
// which is their email — enough for initials and a stable colour.
function userFor(log) {
  const cached = log.userId ? props.userCache.get(log.userId) : null;
  if (cached) return cached;
  if (!log.userName) return null;
  return { name: log.userName, email: log.userName };
}

function whoTooltip(log) {
  if (isAutomated(log)) return `Automation: ${log.automationRuleName || 'rule'}`;
  const user = userFor(log);
  return user?.name || user?.email || 'System';
}

function groupLabel(log) {
  if (log.count > 1) return `${log.count} ${GROUP_LABELS[log.action]}`;
  return getActionDescription(log);
}

// Secondary text for a row. For a collapsed run, only show the note when every
// entry shares it — otherwise the single note would misrepresent the group.
function detailText(log) {
  if (log.count > 1) {
    const notes = new Set(log.children.map((c) => c.note).filter(Boolean));
    return notes.size === 1 ? [...notes][0] : '';
  }
  if (hasSubjectInField(log)) return log.field || '';
  return shouldShowNote(log) ? log.note : '';
}

// Full, untruncated text for the tooltip.
function changeText(log) {
  return `${formatValue(log.field, log.oldValue)} → ${formatValue(log.field, log.newValue)}`;
}

// Only long text gets a tooltip — a tooltip on every short row is noise. Returns
// undefined below the threshold so Vue omits the attribute and tippy skips it.
const TOOLTIP_MIN_LENGTH = 60;
function tooltipIfLong(text) {
  return text && text.length > TOOLTIP_MIN_LENGTH ? text : undefined;
}

const FIELD_LABELS = {
  episodeDescription: 'Show Notes',
  episodeTitle: 'Episode Title',
  episodeThumbnailText: 'Thumbnail Text',
  episodeThumbnailPath: 'Thumbnail',
  episodeThumbnailAssetId: 'Thumbnail',
  episodeThumbnailUrl: 'Thumbnail',
  goLiveDate: 'Publish Date',
  clientReviewDecided: 'Client Review Decided'
};

function formatFieldName(fieldName) {
  if (!fieldName) return '';
  if (FIELD_LABELS[fieldName]) return FIELD_LABELS[fieldName];

  // publishScheduling.<platform>.scheduled → "<Platform> Scheduled"
  const platform = /^publishScheduling\.(\w+)\.scheduled$/.exec(fieldName)?.[1];
  if (platform) return `${platform.charAt(0).toUpperCase()}${platform.slice(1)} Scheduled`;

  // Convert camelCase to readable format
  // e.g., "startDate" -> "Start Date", "assignedUserId" -> "Assigned User ID"
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .replace(/\s+/g, ' ') // Clean up multiple spaces
    .trim();
}

function getActionDescription(log) {
  // Guard against undefined action
  if (!log.action) {
    return 'Activity';
  }

  // Handle contract and user actions - these show field on line 2
  if (log.action.startsWith('contract_')) {
    const actionMap = {
      'contract_created': 'Created Contract',
      'contract_updated': 'Updated Contract',
      'contract_deleted': 'Deleted Contract'
    };
    return actionMap[log.action] || 'Contract Action';
  }

  if (log.action === 'user_added') return 'Added User';
  if (log.action === 'user_removed') return 'Removed User';

  // Handle initial assignment
  if (log.action === 'assigned') {
    return 'Assigned';
  }

  // Handle other actions
  const descriptions = {
    'created': 'Created',
    'work_pool_claim': 'Work Pool Claim',
    'status_change': 'Updated Status',
    'brief_update': 'Updated Brief',
    'stage_change': 'Changed Stage',
    'archived': 'Archived',
    'unarchived': 'Restored',
    'deleted': 'Deleted',
    'event_linked': 'Linked Event',
    'event_unlinked': 'Unlinked Event',
    // Review tool actions
    'review_item_added': 'Added Review Item',
    'review_version_added': 'Added Version',
    'review_version_replaced': 'Replaced Version File',
    'review_item_deleted': 'Deleted Review Item',
    'review_client_removed': 'Removed After Download',
    'review_internal_approved': 'Internal Approved',
    'review_internal_changes_requested': 'Internal Changes Requested',
    'review_internal_unapproved': 'Internal Unapproved',
    'review_resubmitted': 'Resubmitted for Review',
    'review_client_approved': 'Client Approved',
    'review_client_approved_finalization': 'Approved for Finalization',
    'review_client_changes_requested': 'Client Changes Requested',
    'review_comment_added': 'Comment Added',
    'review_reply_added': 'Reply Added',
    'review_comment_deleted': 'Comment Deleted',
    'review_comment_resolved': 'Comment Resolved',
    'review_comment_unresolved': 'Comment Unresolved',
    'review_share_link_created': 'Share Link Created',
    'review_share_link_updated': 'Share Link Updated',
    'review_share_link_deleted': 'Share Link Deleted',
    'review_guest_comment_added': 'Guest Comment',
    'review_guest_reply_added': 'Guest Reply',
    'review_created': 'Review Created',
    'review_internal_reopened': 'Internal Review Reopened',
    'review_client_unapproved': 'Client Unapproved',
    'review_item_renamed': 'Renamed Review Item',
    'review_finalization_required_changed': 'Finalization Requirement Changed',
    'review_finalization_note_updated': 'Finalization Note Updated',
    'review_status_overridden': 'Review Status Overridden',
    'review_version_promoted': 'Sent Version to Client',
    'review_version_demoted': 'Pulled Version from Client',
    'review_finalization_completed': 'Finalization Completed',
    'review_items_reordered': 'Reordered Review Items',
    'review_comment_edited': 'Comment Edited',
    'review_comments_bulk_resolved': 'Comments Bulk Resolved',
    'review_status_reset': 'Review Status Reset',
    'review_bulk_status_set': 'Bulk Review Status Set',
    'review_state_reset': 'Review State Reset',
    'review_slot_changes_flagged': 'Slot Changes Flagged',
    'review_items_cascaded': 'Review Items Updated by Automation',
    'review_asset_promoted': 'Approved Item Added to Library',
    'review_share_link_expired': 'Share Link Expired',
    // Podcast episode actions
    'episode_meta_confirmed': 'Client Confirmed Title & Show Notes',
    'episode_content_kept': 'Kept Episode Content',
    'episode_content_marked_stale': 'Episode Content Marked Stale',
    'episode_transcript_completed': 'Transcript Completed',
    'episode_transcript_failed': 'Transcript Failed',
    'episode_auto_published': 'Episode Published',
    'publish_scheduling_initialized': 'Publish Scheduling Started',
    'publish_date_change_acknowledged': 'Publish Date Change Acknowledged'
  };

  if (descriptions[log.action]) {
    return descriptions[log.action];
  }

  // For simple field_update, include formatted field name in tag
  if (log.action === 'field_update' && log.field) {
    return `Updated ${formatFieldName(log.field)}`;
  }

  return 'Updated';
}

// Contract and user actions stash their subject in `field` (e.g. "Professional
// Plan - Start Date", or the user's email) rather than in `note`.
function hasSubjectInField(log) {
  return log.action === 'contract_updated'
    || log.action === 'user_added'
    || log.action === 'user_removed';
}

// Only meaningful for a single entry. A collapsed run spreads its FIRST child,
// so rendering its old→new would present one item's change as the whole group's.
function shouldShowValueChange(log) {
  return VALUE_CHANGE_ACTIONS.includes(log.action) &&
         log.oldValue !== undefined &&
         log.newValue !== undefined &&
         !valuesEqual(log.oldValue, log.newValue);
}

function shouldShowNote(log) {
  // Review and episode/publish actions carry their human-readable detail in
  // `note` (item title, version label, guest identity), and a work-pool claim
  // names its column. Render it as the detail column so users see what was
  // acted on, not just the action verb. A field_update/created with no old→new
  // to render shows its note ("From episode template") instead.
  if (typeof log.action !== 'string' || !log.note) return false;
  return log.action.startsWith('review_') ||
         log.action.startsWith('episode_') ||
         log.action.startsWith('publish_') ||
         log.action === 'work_pool_claim' ||
         ((log.action === 'field_update' || log.action === 'created') && !shouldShowValueChange(log));
}

function formatValue(field, value) {
  // Handle null/undefined/empty
  if (value === null || value === undefined || value === '') return 'None';

  // Handle booleans
  if (typeof value === 'boolean') {
    const booleanMap = {
      'active': value ? 'Active' : 'Inactive',
      'archived': value ? 'Archived' : 'Active',
      'uvp': value ? 'Enabled' : 'Disabled',
      'legacy': value ? 'Yes' : 'No',
      'days': value ? 'Days' : 'Hours',
      'allDay': value ? 'All Day' : 'Timed Event'
    };
    return booleanMap[field] || (value ? 'Yes' : 'No');
  }

  // Handle arrays (like assignedUsers or tags)
  if (Array.isArray(value)) {
    if (field === 'assignedUsers' || field === 'assignedUserId') {
      // Try to resolve user IDs to names
      return value.map(userId => {
        const user = props.userCache.get(userId);
        return user?.name || user?.email || userId;
      }).join(', ') || 'None';
    }
    return value.length > 0 ? value.join(', ') : 'None';
  }

  // Handle dates and timestamps
  if (field.toLowerCase().includes('date') || field.toLowerCase().includes('time') || value instanceof Date || (typeof value === 'object' && value.toDate)) {
    try {
      const date = value.toDate ? value.toDate() : new Date(value);

      // Check if it's a date with time or just a date
      const hasTime = field.toLowerCase().includes('time') ||
                      (typeof value === 'string' && value.includes('T'));

      if (hasTime) {
        // Format with time (e.g., "Sep 1, 2025 at 1:00 PM")
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } else {
        // Format just the date (e.g., "Sep 1, 2025")
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }
    } catch {
      // If date parsing fails, return as string
      return value.toString();
    }
  }

  // Handle user ID fields - try to resolve to names
  if (field === 'assignedUserId' || field === 'assignedUsers') {
    const user = props.userCache.get(value);
    if (user) {
      return user.name || user.email || value;
    }
  }

  // Handle objects
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  // Deliberately NOT truncated — the row's CSS ellipsis is the single place
  // text gets cut, and the full string goes in the row's tooltip. Truncating
  // here too produced "long title..." clipped again to "long ti…" with no way
  // to read the rest.
  return value.toString();
}

// The change row is one line, so a long OLD value (show notes) would push the
// new value out of view behind the row's ellipsis. Cap each side so both stay
// visible; the full text is in the row tooltip via changeText.
const INLINE_VALUE_MAX = 40;
function formatInlineValue(field, value) {
  const text = formatValue(field, value).replace(/\s+/g, ' ');
  return text.length > INLINE_VALUE_MAX ? `${text.slice(0, INLINE_VALUE_MAX - 1)}…` : text;
}

function formatTimestamp(timestamp) {
  if (!timestamp) return '';

  const date = timestamp instanceof Date ? timestamp :
               (timestamp.toDate ? timestamp.toDate() : new Date(timestamp));
  const now = new Date();
  const diff = now - date;

  // Less than a minute
  if (diff < 60000) {
    return 'Just now';
  }

  // Less than an hour
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }

  // Less than a day
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }

  // Less than a week
  if (diff < 604800000) {
    const days = Math.floor(diff / 86400000);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }

  // A prior year drops the clock time. Nobody needs minute precision on a
  // year-old entry, and "Jul 14, 2025, 10:24 AM" would be the longest string
  // the column ever holds — forcing every row to reserve width for a format
  // that almost never appears.
  if (date.getFullYear() !== now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  // Date + time. The clock time is what makes a burst readable — "Jul 30,
  // 5:52 AM" next to "Jul 30, 5:55 AM" shows the client acted twice in three
  // minutes, which a bare "Jul 30" on both rows hides.
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}
</script>

<style scoped lang="scss">
.activity-timeline {
  padding: 0.5rem 0;
  height: 100%;
  overflow-y: auto;
}

.tl-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid var(--bulma-border-weak);
  font-size: 0.8125rem;
  line-height: 1.4;

  &.is-expandable {
    cursor: pointer;
  }

  &:hover {
    background-color: var(--bulma-scheme-main-ter);
  }
}

// Symmetric padding around a 24px avatar. Combined with the right-aligned time
// column, this is the only thing setting the gutter, so both sides match.
.tl-who {
  flex: 0 0 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

// Automation gets a mark, not an avatar — it isn't a person, and the rule name
// lives in the tooltip so the row stays the same width as every other row.
.tl-bot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bulma-scheme-main-ter);
  border: 1px solid var(--bulma-border-weak);
  color: var(--bulma-text-weak);
  font-size: 0.6875rem;
}

.tl-action {
  flex: 0 0 auto;
  width: 12rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Detail and change columns absorb the leftover width and truncate, so every
// row stays exactly one line no matter how long the item title is.
.tl-detail,
.tl-change {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--bulma-text-weak);
}

.tl-change {
  font-family: monospace;
  font-size: 0.75rem;
}

.tl-caret {
  flex: 0 0 auto;
  font-size: 0.625rem;
  color: var(--bulma-text-weak);
}

// Right aligned so the gutter around the avatar is symmetric. The gap left of
// the avatar is (unused width in this column + row gap + .tl-who padding) while
// the gap on its right is only (.tl-who padding + row gap) — so any slack here
// lands entirely on the avatar's left and makes it look off-centre. Aligning
// right pins the text to the column's inner edge, zeroing that slack term.
//
// Width is set for the common case rather than the worst; a rare longer stamp
// overflows leftward into the outer margin, which shifts nothing else in the row.
.tl-time {
  flex: 0 0 6rem;
  text-align: right;
  font-size: 0.6875rem;
  color: var(--bulma-text-weak);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.tl-children {
  border-bottom: 1px solid var(--bulma-border-weak);
}

.tl-child {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
}

.tl-gap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.5rem;
  color: var(--bulma-text-weak);
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: var(--bulma-border-weak);
  }
}
</style>
